import { caAtom, caCheckAtom } from '@/atoms/caCheck';
import { loadingAtom } from '@/atoms/loadingAtom';
import { rugWalletAtom } from '@/atoms/rugWallet';
import { tgMetricsAtom } from '@/atoms/tgSentiment';
import { tokenInfoAtom } from '@/atoms/tokenInfo';
import { xAtom } from '@/atoms/xAtom';
import { useTrackView } from '@/hooks/useTrackView';
import { api } from '@/lib/api-client';
import { RugWalletResponse } from '@/types/rugWalletResponse';
import { TGMetricsResponse } from '@/types/tgMetrics';
import { XSensationResponse } from '@/types/x';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { useApiQuery } from './useApiQuery';
import { useResetSearch } from './useResetSearch';

// Query keys for caching and deduplication
export const queryKeys = {
  checkCA: (ca: string) => ['check-ca', ca],
  tokenInfo: (ca: string) => ['token-info', ca],
  tgMetrics: (ca: string) => ['tg-metrics', ca],
  rugWallet: (wallet: string) => ['rug-wallet', wallet],
  xSensation: (username: string) => ['x-sensation', username],
} as const;

export function useTokenSearch() {
  const [ca, setCA] = useAtom(caAtom);
  const setCheckCAResult = useSetAtom(caCheckAtom);
  const setTGMetricsResult = useSetAtom(tgMetricsAtom);
  const setTokenInfoResult = useSetAtom(tokenInfoAtom);
  const setXInfoResult = useSetAtom(xAtom);
  const setRugWalletResult = useSetAtom(rugWalletAtom);
  const [_, setIsLoading] = useAtom(loadingAtom);
  const { resetSearch } = useResetSearch();
  const trackView = useTrackView();
  const queryClient = useQueryClient();

  // Only fetch when CA is available
  const enabled = Boolean(ca);

  const { data: checkCAData, isLoading: isCheckCALoading } = useApiQuery(
    queryKeys.checkCA(ca),
    async () => {
      const data = await api.checkCA(ca);
      setCheckCAResult(data);
      return data;
    },
    {
      enabled,
      staleTime: 30000,
    },
  );

  const { data: tokenInfoData, isLoading: isTokenInfoLoading } = useApiQuery(
    queryKeys.tokenInfo(ca),
    async () => {
      setIsLoading(true);
      const data = await api.getTokenInfo(ca);
      setTokenInfoResult(data.check_ca);

      trackView.mutate({
        token_address: data.check_ca.ca,
      });

      // Prefetch related data
      if (data.check_ca.creator) {
        queryClient.prefetchQuery({
          queryKey: queryKeys.rugWallet(data.check_ca.creator),
          queryFn: () => fetchRugWalletInfo(data.check_ca.creator),
        });
      }

      if (data.check_ca.ca && data.check_ca.tg) {
        queryClient.prefetchQuery({
          queryKey: queryKeys.tgMetrics(data.check_ca.ca),
          queryFn: () => fetchTgMetrics(data.check_ca.ca),
        });
      }

      if (data.check_ca.x) {
        const cleanUsername = data.check_ca.x.split('/').at(-1);
        if (cleanUsername) {
          queryClient.prefetchQuery({
            queryKey: queryKeys.xSensation(cleanUsername),
            queryFn: () => fetchXInfo(cleanUsername),
          });
        }
      }
      setIsLoading(false);
      return data;
    },
    {
      enabled,
      staleTime: 30000,
    },
  );

  async function fetchRugWalletInfo(wallet: string): Promise<RugWalletResponse> {
    const response = await api.checkRugWallet(wallet);
    setRugWalletResult(response);
    return response;
  }

  async function fetchXInfo(username: string): Promise<XSensationResponse> {
    const response = await api.xSensation(username);
    setXInfoResult(response);
    return response;
  }

  async function fetchTgMetrics(ca: string): Promise<TGMetricsResponse> {
    const response = await api.tgMetrics(ca);
    setTGMetricsResult(response);
    return response;
  }

  const handleSearch = (formattedCA: string) => {
    resetSearch();
    setCA(formattedCA);
    setIsLoading(true);
  };

  const handleSearchAndNavigate = (formattedCA: string) => {
    resetSearch();
    setCA(formattedCA);
    setIsLoading(true);
    window.history.pushState(null, '', `/ca/${formattedCA}`);
  };

  function invalidateTokenQueries(ca: string) {
    queryClient.invalidateQueries({ queryKey: queryKeys.checkCA(ca) });
    queryClient.invalidateQueries({ queryKey: queryKeys.tokenInfo(ca) });
    queryClient.invalidateQueries({ queryKey: queryKeys.tgMetrics(ca) });
    // Optionally, invalidate related queries if needed (e.g., rugWallet, xSensation)
  }

  useEffect(() => {
    setIsLoading(isCheckCALoading || isTokenInfoLoading);
  }, [isCheckCALoading, isTokenInfoLoading]);

  return {
    ca,
    setCA,
    handleSearch,
    handleSearchAndNavigate,
    invalidateTokenQueries,
  };
}
